import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Store, Bike, ShieldCheck, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { OtpPanel } from "@/components/common/OtpPanel";
import { AuthShell, AuthBrandHeader } from "@/components/common/AuthShell";
import { InstallCard } from "@/components/customer/InstallHint";

const ROLES = [
  { key: "customer", label: "Customer", icon: User },
  { key: "restaurant", label: "Restaurant", icon: Store },
  { key: "delivery", label: "Delivery", icon: Bike },
];
const TITLES = { customer: "Customer Login", restaurant: "Restaurant Login", delivery: "Delivery Partner Login" };
const REG = {
  customer: { label: "New to BiteGoFood?", cta: "Create New Account", to: "/signup" },
  restaurant: { label: "New Restaurant Partner?", cta: "Register Your Restaurant", to: "/partner/register" },
  delivery: { label: "New Delivery Partner?", cta: "Register as Delivery Partner", to: "/rider/register" },
};

function GoogleButton({ role }) {
  const startGoogle = () => {
    if (role !== "customer") localStorage.setItem("bitego_google_role", role);
    else localStorage.removeItem("bitego_google_role");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={startGoogle} className="w-full bg-white border border-border hover:border-ink-600/40 text-ink font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors tap" data-testid="google-login-btn">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4" /> Continue with Google
    </button>
  );
}

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [role, setRole] = useState("customer");
  const [phone, setPhone] = useState("");
  const [step, setStep] = useState("phone");
  const dest = { customer: "/", restaurant: "/partner", delivery: "/rider" };

  const redirectByUser = (u) => {
    if (u.role === "restaurant") return nav(u.active ? "/partner" : "/partner/register");
    if (u.role === "delivery") return nav(u.active || u.kyc_verified ? "/rider" : "/rider/register");
    return nav(dest[u.role] || "/");
  };

  const startOtp = () => {
    if (phone.length < 10) return toast.error("Enter a valid 10-digit phone number");
    setStep("otp");
  };
  const onVerified = (data) => { login(data.token, data.user); toast.success("Welcome to BiteGoFood!"); redirectByUser(data.user); };

  const reg = REG[role];

  return (
    <AuthShell maxWidth="440px">
      <AuthBrandHeader />

      {/* Role selector — premium segmented control */}
      <div className="grid grid-cols-3 gap-2 mb-5" role="tablist">
        {ROLES.map((r) => {
          const active = role === r.key;
          return (
            <button key={r.key} role="tab" onClick={() => { setRole(r.key); setStep("phone"); }}
              className={`flex flex-col items-center justify-center gap-1.5 h-[70px] rounded-2xl border transition-all duration-200 tap ${active ? "bg-coral text-white border-coral shadow-md shadow-coral/25 scale-[1.02]" : "bg-white text-ink border-border hover:border-coral/40 hover:bg-coral-light/40"}`}
              data-testid={`role-selector-${r.key}`}>
              <r.icon size={20} className={active ? "text-white" : "text-coral"} />
              <span className="text-xs font-bold leading-none">{r.label}</span>
            </button>
          );
        })}
      </div>

      {/* Auth card */}
      <div className="bg-white border border-border rounded-3xl shadow-sm p-6 sm:p-7">
        {step === "otp" ? (
          <OtpPanel phone={phone} role={role} title={TITLES[role]} onBack={() => setStep("phone")} onVerified={onVerified} />
        ) : (
          <div className="space-y-4">
            <div>
              <h2 className="font-display font-extrabold text-2xl text-ink tracking-tight">Welcome back</h2>
              <p className="text-sm text-ink-600 mt-1">Sign in to continue to BiteGoFood</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-600">Mobile Number</label>
              <div className="flex mt-1.5">
                <span className="bg-surface border border-border border-r-0 rounded-l-xl px-3.5 flex items-center text-sm font-semibold text-ink-600">+91</span>
                <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} onKeyDown={(e) => e.key === "Enter" && startOtp()} placeholder="Enter 10-digit mobile number" inputMode="numeric" className="flex-1 min-w-0 bg-surface rounded-r-xl px-3.5 py-3.5 text-sm outline-none focus:ring-2 ring-coral focus:bg-white transition-all" data-testid="phone-input" />
              </div>
            </div>
            <button onClick={startOtp} className="w-full bg-coral hover:bg-coral-hover active:scale-[0.99] text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all shadow-sm shadow-coral/30 tap" data-testid="send-otp-btn">
              Continue <ArrowRight size={18} />
            </button>
            <div className="flex items-center gap-3"><div className="flex-1 h-px bg-border" /><span className="text-[11px] font-medium text-ink-600">OR</span><div className="flex-1 h-px bg-border" /></div>
            <GoogleButton role={role} />
            {reg && (
              <div className="pt-1 flex items-center justify-between gap-3 rounded-xl bg-surface border border-border px-4 py-3">
                <span className="text-xs text-ink-600">{reg.label}</span>
                <button onClick={() => nav(reg.to)} className="text-sm font-bold text-coral hover:text-coral-hover flex items-center gap-1 transition-colors tap" data-testid="create-account-btn">{reg.cta} <ArrowRight size={15} /></button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Native browser install suggestion — only appears when installable */}
      <InstallCard />

      {/* Footer */}
      <div className="mt-6 flex flex-col items-center gap-2">
        <p className="text-center text-[11px] text-ink-600">A Product by Vijay Software Solutions Pvt Ltd</p>
        <div className="flex items-center gap-2 text-[11px] text-ink-600">
          <button onClick={() => nav("/privacy")} className="hover:text-coral transition-colors tap" data-testid="privacy-link">Privacy Policy</button>
          <span className="text-ink-600/40">·</span>
          <button onClick={() => nav("/terms")} className="hover:text-coral transition-colors tap" data-testid="terms-link">Terms</button>
        </div>
        <button onClick={() => nav("/admin/login")} aria-label="Secure access" title="Secure access"
          className="flex items-center gap-1 text-[10px] text-ink-600/50 hover:text-ink-600 transition-colors" data-testid="secure-access-link">
          <ShieldCheck size={11} /> Secure access
        </button>
      </div>
    </AuthShell>
  );
}
